import { default as assert } from 'assert';
import { isEqual, isPlainObject } from 'lodash';

/**
 * Simple model class. Could be used for anything; its main functionality
 * is to store the data object passed to the constructor as ``._data`
 */
export default class Model<T> {

  protected _data: T;

  /**
   * Create model instance. Data is stored in _data.
   * @param {object} data
   */
  constructor(data: T) {
    assert(isPlainObject(data), 'invalid data');

    this._data = data;
  }

  /**
   * Create an instance of this model, or return an existing
   * instance with identical data if one exists.
   *
   * Only one cached copy is stored per key.
   */
  static memoizeByKey(key: any, data: any) {
    assert(key, 'missing key');
    assert(isPlainObject(data), 'invalid data');

    const ModelClass = this;
    const memo = ((ModelClass as any)._memosByKey = (ModelClass as any)._memosByKey || new Map());
    const existing = memo.get(key);

    if (existing && isEqual(existing._data, data)) {
      return existing;
    } else {
      // Set or replace cached instance
      const instance = new ModelClass(data);
      memo.set(key, instance);
      return instance;
    }
  }

  createGetters(fields: string[]) {
    assert(Array.isArray(fields), 'invalid fields, should be an array');

    for (const field of fields) {
      Object.defineProperty(this, field, {
        get: () => this._get(field)
      });
    }
  }

  /**
   * @returns {object}
   */
  toJSON() {
    // Shallow copy
    return Object.assign({}, this._data);
  }

  /**
   * Shallow clone this model with updated properties.
   *
   * @param {object} [updates] - map of key/value updates to apply;
   *   undefined values are skipped
   * @returns {Model}
   */
  withUpdates(updates: any = {}) {
    const ModelClass = this.constructor;
    const data: any = this.toJSON(); // shallow clone

    for (const key in updates) {
      const value = updates[key];
      if (value !== undefined) {
        data[key] = value;
      }
    }

    return new (ModelClass as any)(data);
  }

  _get(key: string) {
    return (this._data as any)[key];
  }
}
